<script setup>
// Calendario completo do filtro de periodo (modelado e aprovado no
// prototipo): presets a esquerda, dois meses navegaveis, selecao por
// clique e "Voltar ao padrao". Nada e aplicado ate clicar em Aplicar.
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import format from 'date-fns/format';
import addMonths from 'date-fns/addMonths';
import subMonths from 'date-fns/subMonths';
import startOfMonth from 'date-fns/startOfMonth';
import endOfMonth from 'date-fns/endOfMonth';
import startOfWeek from 'date-fns/startOfWeek';
import endOfWeek from 'date-fns/endOfWeek';
import eachDayOfInterval from 'date-fns/eachDayOfInterval';
import isSameMonth from 'date-fns/isSameMonth';
import isSameDay from 'date-fns/isSameDay';
import {
  useReportsContext,
  PERIOD_PRESETS,
  DEFAULT_PERIOD,
} from '../composables/useReportsContext';

const emit = defineEmits(['close']);

const { t, locale } = useI18n();
const intlLocale = computed(() => (locale.value || 'pt_BR').replace('_', '-'));
const { state, range, setPeriod, setCustomPeriod, resetPeriod } =
  useReportsContext();

const ISO = 'yyyy-MM-dd';
const today = new Date();

// Rascunho: so vai para o contexto global quando o usuario aplica
const draft = ref({
  key: state.periodKey,
  from: format(range.value.from, ISO),
  to: format(range.value.to, ISO),
});
const pending = ref(null); // 1o clique de um intervalo novo

const viewMonth = ref(subMonths(startOfMonth(range.value.to), 1));

const monthsShown = computed(() => [
  viewMonth.value,
  addMonths(viewMonth.value, 1),
]);

const buildMonth = base => {
  const days = eachDayOfInterval({
    start: startOfWeek(startOfMonth(base)),
    end: endOfWeek(endOfMonth(base)),
  });
  return {
    label: base.toLocaleDateString(intlLocale.value, {
      month: 'long',
      year: 'numeric',
    }),
    days: days.map(day => {
      const iso = format(day, ISO);
      return {
        iso,
        label: day.getDate(),
        outside: !isSameMonth(day, base),
        isToday: isSameDay(day, today),
        isEdge: iso === draft.value.from || iso === draft.value.to,
        inRange: iso > draft.value.from && iso < draft.value.to,
      };
    }),
  };
};

const calendars = computed(() => monthsShown.value.map(buildMonth));

const weekDays = computed(() =>
  eachDayOfInterval({ start: startOfWeek(today), end: endOfWeek(today) }).map(
    day =>
      day
        .toLocaleDateString(intlLocale.value, { weekday: 'short' })
        .replace('.', '')
        .slice(0, 3)
  )
);

const shiftMonths = amount => {
  viewMonth.value = addMonths(viewMonth.value, amount);
};

const pickPreset = key => {
  const preset = PERIOD_PRESETS.find(item => item.key === key);
  if (!preset) return;
  const [from, to] = preset.range(today);
  draft.value = { key, from: format(from, ISO), to: format(to, ISO) };
  pending.value = null;
  viewMonth.value = subMonths(startOfMonth(to), 1);
};

// 1o clique fixa o inicio, 2o fecha o intervalo (inverte se for para tras)
const pickDay = day => {
  if (day.outside) return;
  if (!pending.value) {
    pending.value = day.iso;
    draft.value = { key: 'custom', from: day.iso, to: day.iso };
    return;
  }
  const [from, to] =
    day.iso < pending.value
      ? [day.iso, pending.value]
      : [pending.value, day.iso];
  draft.value = { key: 'custom', from, to };
  pending.value = null;
};

const summary = computed(() => {
  const parse = iso => format(new Date(`${iso}T00:00:00`), 'dd/MM/yyyy');
  return `${parse(draft.value.from)} → ${parse(draft.value.to)}`;
});

const isDraftDefault = computed(() => draft.value.key === DEFAULT_PERIOD);

const apply = () => {
  if (draft.value.key === 'custom') {
    setCustomPeriod(draft.value.from, draft.value.to);
  } else {
    setPeriod(draft.value.key);
  }
  emit('close');
};

const backToDefault = () => {
  resetPeriod();
  emit('close');
};
</script>

<template>
  <div class="flex rounded-xl border border-n-weak bg-n-solid-2 shadow-xl">
    <div class="w-[168px] flex-none border-r border-n-weak p-2">
      <div
        class="px-2.5 pt-1 pb-2 text-[10px] font-semibold uppercase tracking-wider text-n-slate-9"
      >
        {{ t('REPORTS_CONTEXT.PERIOD') }}
      </div>
      <button
        v-for="preset in PERIOD_PRESETS"
        :key="preset.key"
        class="w-full text-left px-2.5 py-1.5 rounded-lg text-xs hover:bg-n-alpha-1"
        :class="
          draft.key === preset.key
            ? 'bg-[#9B2C2C]/10 text-[#9B2C2C] dark:bg-[#F472B6]/15 dark:text-[#F472B6] font-semibold'
            : 'text-n-slate-11'
        "
        @click="pickPreset(preset.key)"
      >
        {{ t(`REPORTS_CONTEXT.PRESETS.${preset.key.toUpperCase()}`) }}
      </button>
      <div
        class="w-full px-2.5 py-1.5 rounded-lg text-xs"
        :class="
          draft.key === 'custom'
            ? 'bg-[#9B2C2C]/10 text-[#9B2C2C] dark:bg-[#F472B6]/15 dark:text-[#F472B6] font-semibold'
            : 'text-n-slate-9'
        "
      >
        {{ t('REPORTS_CONTEXT.CUSTOM') }}
      </div>
    </div>

    <div class="p-3.5">
      <div class="grid grid-cols-[26px_1fr_1fr_26px] items-center mb-2">
        <button
          class="h-[26px] rounded-lg text-n-slate-10 hover:bg-n-alpha-1 hover:text-n-slate-12"
          @click="shiftMonths(-1)"
        >
          <span class="i-lucide-chevron-left size-4 mx-auto" />
        </button>
        <span
          v-for="calendar in calendars"
          :key="calendar.label"
          class="text-center text-[13px] font-semibold text-n-slate-12 capitalize"
        >
          {{ calendar.label }}
        </span>
        <button
          class="h-[26px] rounded-lg text-n-slate-10 hover:bg-n-alpha-1 hover:text-n-slate-12"
          @click="shiftMonths(1)"
        >
          <span class="i-lucide-chevron-right size-4 mx-auto" />
        </button>
      </div>

      <div class="flex gap-4">
        <div
          v-for="calendar in calendars"
          :key="calendar.label"
          class="w-[204px]"
        >
          <div class="grid grid-cols-7">
            <span
              v-for="(weekDay, idx) in weekDays"
              :key="idx"
              class="text-center text-[9px] font-bold uppercase tracking-wide text-n-slate-9 pb-1"
            >
              {{ weekDay }}
            </span>
          </div>
          <div class="grid grid-cols-7">
            <button
              v-for="day in calendar.days"
              :key="day.iso"
              class="h-7 text-xs tabular-nums"
              :class="[
                day.outside
                  ? 'text-n-slate-9/50 cursor-default'
                  : 'text-n-slate-11 hover:bg-n-alpha-1',
                day.inRange && !day.outside ? 'bg-n-alpha-1' : '',
                day.isEdge && !day.outside
                  ? 'bg-n-brand text-white font-bold rounded-lg'
                  : '',
                day.isToday && !day.isEdge
                  ? 'rounded-lg outline outline-1 -outline-offset-2 outline-[#BE185D]'
                  : '',
              ]"
              @click="pickDay(day)"
            >
              {{ day.label }}
            </button>
          </div>
        </div>
      </div>

      <div
        class="flex items-center gap-2 mt-3 pt-3 border-t border-n-weak flex-wrap"
      >
        <span class="text-xs text-n-slate-10">{{ summary }}</span>
        <span class="flex-1" />
        <button
          v-if="!isDraftDefault"
          class="px-2.5 py-1.5 text-xs rounded-lg border border-n-weak text-n-slate-10 hover:text-n-slate-12"
          @click="backToDefault"
        >
          {{ t('REPORTS_CONTEXT.BACK_TO_DEFAULT') }}
        </button>
        <button
          class="px-2.5 py-1.5 text-xs rounded-lg border border-n-weak text-n-slate-10 hover:text-n-slate-12"
          @click="emit('close')"
        >
          {{ t('REPORTS_CONTEXT.CANCEL') }}
        </button>
        <button
          class="px-3 py-1.5 text-xs font-semibold text-white rounded-lg bg-n-brand hover:brightness-110"
          @click="apply"
        >
          {{ t('REPORTS_CONTEXT.APPLY') }}
        </button>
      </div>
    </div>
  </div>
</template>
